
import React from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, Loader2 } from 'lucide-react';
import { useAudioPlayback } from '@/hooks/useAudioPlayback';
import { Progress } from '@/components/ui/progress';
import { debugLogger } from '@/utils/debugLogger';

interface OptimizedAudioPlayerProps {
  src: string;
  messageId: string;
  audioDuration?: number;
  darkIndicator?: boolean;
  className?: string;
}

/**
 * Safe time formatting with validation
 */
const formatTime = (seconds: number): string => {
  // Validate input
  if (!Number.isFinite(seconds) || seconds < 0 || isNaN(seconds)) {
    return '0:00';
  }
  
  // Ensure we have a valid positive number
  const validSeconds = Math.max(0, Math.floor(seconds));
  
  const mins = Math.floor(validSeconds / 60);
  const secs = validSeconds % 60;
  
  // Handle edge cases for very large numbers
  if (mins > 999) {
    return '999:59';
  }
  
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Safe progress calculation with validation
 */
const calculateProgress = (currentTime: number, duration: number): number => {
  // Validate inputs
  if (!Number.isFinite(currentTime) || !Number.isFinite(duration) || 
      currentTime < 0 || duration <= 0 || 
      isNaN(currentTime) || isNaN(duration)) {
    return 0;
  }
  
  const progress = (currentTime / duration) * 100;
  
  // Ensure progress is within valid bounds
  return Math.max(0, Math.min(100, progress));
};

export const OptimizedAudioPlayer: React.FC<OptimizedAudioPlayerProps> = ({
  src,
  messageId,
  audioDuration = 0,
  darkIndicator = false,
  className = ''
}) => {
  // Validate and use the duration from database immediately
  const validatedDatabaseDuration = React.useMemo(() => {
    const validated = calculateProgress(audioDuration, 1) > 0 ? audioDuration : 0;
    debugLogger.debug('OptimizedAudioPlayer', 'Using database duration:', {
      messageId,
      originalDuration: audioDuration,
      validatedDuration: validated
    });
    return validated;
  }, [audioDuration, messageId]);

  const {
    isPlaying,
    currentTime,
    duration,
    isLoading,
    error,
    hasSignedUrl,
    isConverting,
    conversionProgress,
    toggle,
    seekTo
  } = useAudioPlayback({
    audioId: messageId,
    mediaUrl: src,
    audioDuration: validatedDatabaseDuration
  });

  // Use database duration if available, fallback to hook duration
  const displayDuration = React.useMemo(() => {
    const finalDuration = validatedDatabaseDuration > 0 ? validatedDatabaseDuration : duration;
    debugLogger.debug('OptimizedAudioPlayer', 'Display duration determined:', {
      messageId,
      databaseDuration: validatedDatabaseDuration,
      hookDuration: duration,
      finalDuration
    });
    return finalDuration;
  }, [validatedDatabaseDuration, duration, messageId]);

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isConverting || displayDuration <= 0) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const newTime = (clickX / width) * displayDuration;
    
    if (Number.isFinite(newTime) && newTime >= 0 && newTime <= displayDuration) {
      seekTo(newTime);
    }
  };

  const handlePlayPauseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  };

  const progressPercentage = calculateProgress(currentTime, displayDuration);

  if (error) {
    return (
      <div className={`flex items-center space-x-2 p-2 bg-red-500/20 rounded-lg ${className}`}>
        <div className="text-red-300 text-xs">
          Erro ao carregar áudio
        </div>
      </div>
    );
  }

  if ((isLoading || !hasSignedUrl) && !isConverting) {
    return (
      <div className={`flex items-center space-x-2 p-2 bg-white/5 rounded-lg ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin text-white/50" />
        <div className="flex-1 bg-white/10 h-1 rounded-full animate-pulse" />
        <div className="text-white/50 text-xs">Carregando...</div>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-3 p-2 bg-white/5 rounded-lg ${className}`}>
      {/* Play/Pause Button */}
      <Button
        onClick={handlePlayPauseClick}
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 rounded-full bg-white/10 hover:bg-white/20 text-white play-pause-button audio-control"
        disabled={isConverting}
        data-interactive="true"
      >
        {isConverting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isPlaying ? (
          <Pause className="h-4 w-4" />
        ) : (
          <Play className="h-4 w-4 ml-0.5" />
        )}
      </Button>

      {/* Progress Bar */}
      <div className="flex-1 flex flex-col space-y-1">
        {/* Conversion Progress */}
        {isConverting && (
          <div className="space-y-1">
            <div className="text-xs text-white/70 text-center">
              Convertendo para iOS... {Math.round(conversionProgress)}%
            </div>
            <Progress value={conversionProgress} className="h-1" />
          </div>
        )}

        {/* Audio Progress */}
        {!isConverting && (
          <div
            className="relative h-1 bg-white/20 rounded-full cursor-pointer group audio-progress-bar progress-control"
            onClick={handleProgressClick}
            data-interactive="true"
          >
            <div
              className="absolute left-0 top-0 h-full bg-white/60 rounded-full transition-all duration-150"
              style={{ width: `${progressPercentage}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-150"
              style={{ left: `${progressPercentage}%`, transform: 'translateX(-50%) translateY(-50%)' }}
            />
          </div>
        )}

        {/* Time Display */}
        {!isConverting && (
          <div className="flex justify-between text-xs text-white/70">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(displayDuration)}</span>
          </div>
        )}
      </div>

      {/* Audio Wave Indicator */}
      <div className="flex items-center space-x-0.5">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className={`w-0.5 bg-white/40 rounded-full transition-all duration-150 ${
              isPlaying && !isConverting ? 'animate-pulse' : ''
            }`}
            style={{
              height: isPlaying && !isConverting ? `${8 + (i * 2)}px` : '6px',
              animationDelay: `${i * 100}ms`
            }}
          />
        ))}
      </div>
    </div>
  );
};
